import React, { useEffect, useRef, useState } from 'react';
import { Plus, X, Paperclip, Trash2, Pin, Save, FileText } from 'lucide-react';
import api, { API } from '../lib/api';
import { toast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { NOTE_COLORS } from './NoteCard';
import { compressPhoto } from '../lib/photo';

const inp = 'w-full bg-muted/40 border border-border rounded-[3px] px-2.5 py-2 text-[13px] outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/50';

export const NoteEditor = ({ note, open, onClose, onSaved }) => {
  const fileRef = useRef(null);
  const [form, setForm] = useState({ title: '', body: '', items: [], color: 'default', pinned: false });
  const [newItem, setNewItem] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [atts, setAtts] = useState([]);
  const [id, setId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      title: note?.title || '', body: note?.body || '', items: note?.items || [],
      color: note?.color || 'default', pinned: !!note?.pinned,
    });
    setAtts(note?.attachments || []);
    setId(note?.id || null);
    setNewItem('');
  }, [open, note]);

  const addItem = () => {
    const text = newItem.trim();
    if (!text) return;
    setForm((f) => ({ ...f, items: [...f.items, { id: `tmp-${Date.now()}`, text, done: false }] }));
    setNewItem('');
  };
  const toggleItem = (iid) => setForm((f) => ({ ...f, items: f.items.map((i) => (i.id === iid ? { ...i, done: !i.done } : i)) }));
  const delItem = (iid) => setForm((f) => ({ ...f, items: f.items.filter((i) => i.id !== iid) }));

  const persist = async () => {
    if (!form.title.trim() && !form.body.trim() && !form.items.length) {
      toast({ title: 'Nota vazia', description: 'Escreva um título, um texto ou uma tarefa.' });
      return null;
    }
    setSaving(true);
    try {
      const { data } = id ? await api.put(`/notes/${id}`, form) : await api.post('/notes', form);
      setId(data.id);
      return data;
    } catch (e) {
      toast({ title: 'Erro ao salvar', description: e.response?.data?.detail || 'Tente novamente.' });
      return null;
    } finally { setSaving(false); }
  };

  const save = async () => {
    const saved = await persist();
    if (!saved) return;
    toast({ title: id ? 'Nota atualizada' : 'Nota criada' });
    onSaved && onSaved();
    onClose();
  };

  const attach = async (file) => {
    if (!file) return;
    let target = id;
    if (!target) {
      const saved = await persist();
      if (!saved) return;
      target = saved.id;
    }
    setUploading(true);
    try {
      const prepared = file.type?.startsWith('image/') ? await compressPhoto(file) : file;
      const fd = new FormData();
      fd.append('file', prepared, prepared.name || file.name);
      const { data } = await api.post(`/notes/${target}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
      setAtts((a) => [...a, data]);
      onSaved && onSaved();
    } catch (e) {
      toast({ title: 'Erro no anexo', description: e.response?.data?.detail || 'Tente novamente.' });
    } finally { setUploading(false); }
  };

  const delAtt = async (aid) => {
    try {
      await api.delete(`/notes/${id}/attachments/${aid}`);
      setAtts((a) => a.filter((x) => x.id !== aid));
      onSaved && onSaved();
    } catch (e) { toast({ title: 'Erro ao remover anexo', description: e.response?.data?.detail || '' }); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-2xl"
        style={{ borderTopWidth: 2, borderTopColor: NOTE_COLORS[form.color].bar }}>
        <DialogHeader>
          <DialogTitle className="ent-coltitle uppercase tracking-wide">{id ? 'Editar nota' : 'Nova nota'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <input data-testid="note-input-title" className={`${inp} text-[15px] font-bold`} placeholder="Título"
            value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <textarea data-testid="note-input-body" rows={6} className={`${inp} leading-relaxed resize-y`}
            placeholder="Escreva sua nota..." value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />

          <div className="space-y-1.5">
            <p className="ent-label">Tarefas</p>
            {form.items.map((it) => (
              <div key={it.id} className="flex items-center gap-2" data-testid={`note-item-${it.id}`}>
                <input type="checkbox" checked={!!it.done} onChange={() => toggleItem(it.id)}
                  data-testid={`note-item-check-${it.id}`} className="accent-[hsl(var(--primary))] w-3.5 h-3.5" />
                <span className={`flex-1 text-[13px] ${it.done ? 'line-through text-muted-foreground' : ''}`}>{it.text}</span>
                <button data-testid={`note-item-del-${it.id}`} onClick={() => delItem(it.id)}
                  className="p-1 text-muted-foreground hover:text-[hsl(var(--bad))] transition-colors"><X className="w-3 h-3" /></button>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <input data-testid="note-item-input" className={inp} placeholder="Adicionar tarefa e pressionar Enter"
                value={newItem} onChange={(e) => setNewItem(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }} />
              <button data-testid="note-item-add" onClick={addItem} className="ent-btn ent-btn-sm shrink-0"><Plus className="w-3 h-3" /> Add</button>
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <p className="ent-label">Anexos ({atts.length}/10)</p>
              <button data-testid="note-attach-btn" disabled={uploading || atts.length >= 10} onClick={() => fileRef.current?.click()}
                className="ent-btn ent-btn-sm disabled:opacity-50"><Paperclip className="w-3 h-3" /> {uploading ? 'Enviando...' : 'Anexar'}</button>
              <input ref={fileRef} type="file" className="hidden" data-testid="note-attach-input"
                accept="image/*,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx"
                onChange={(e) => { attach(e.target.files?.[0]); e.target.value = ''; }} />
            </div>
            {atts.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="note-attachments">
                {atts.map((a) => (
                  <div key={a.id} className="relative group rounded-[3px] border border-border overflow-hidden bg-muted/30">
                    <a href={`${API}/notes/${id}/attachments/${a.id}`} target="_blank" rel="noreferrer" title={a.filename}>
                      {a.is_image
                        ? <img src={`${API}/notes/${id}/attachments/${a.id}`} alt="" className="w-full aspect-square object-cover" />
                        : <div className="w-full aspect-square flex flex-col items-center justify-center gap-1 p-1">
                            <FileText className="w-5 h-5 text-muted-foreground" />
                            <span className="text-[9px] t-label truncate w-full text-center">{a.filename}</span>
                          </div>}
                    </a>
                    <button data-testid={`note-att-del-${a.id}`} onClick={() => delAtt(a.id)}
                      className="absolute top-1 right-1 bg-black/70 rounded p-0.5 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <p className="ent-label mr-1">Cor</p>
            {Object.entries(NOTE_COLORS).map(([key, c]) => (
              <button key={key} data-testid={`note-color-${key}`} title={key} onClick={() => setForm({ ...form, color: key })}
                className={`w-5 h-5 rounded-full border transition-transform ${form.color === key ? 'ring-2 ring-primary scale-110' : 'border-border'}`}
                style={{ background: c.bar }} />
            ))}
            <button data-testid="note-pin-toggle" onClick={() => setForm({ ...form, pinned: !form.pinned })}
              className={`ent-btn ent-btn-sm ml-auto ${form.pinned ? 'text-primary border-primary/50' : ''}`}>
              <Pin className="w-3 h-3" style={form.pinned ? { fill: 'currentColor' } : undefined} /> {form.pinned ? 'Fixada' : 'Fixar'}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
          {id && (
            <button data-testid="note-editor-delete" onClick={async () => {
              await api.delete(`/notes/${id}`); toast({ title: 'Nota excluída' }); onSaved && onSaved(); onClose();
            }} className="ent-btn ent-btn-sm mr-auto hover:text-[hsl(var(--bad))]"><Trash2 className="w-3 h-3" /> Excluir</button>
          )}
          <button data-testid="note-editor-cancel" onClick={onClose} className="ent-btn ent-btn-sm">Cancelar</button>
          <button data-testid="note-editor-save" onClick={save} disabled={saving} className="ent-btn ent-btn-primary">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
